import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PreprintLayer } from "@/components/PreprintLayer";
import { DEFAULT_ALIGNMENT } from "@/lib/alignment";
import { downloadPdf, shareWhatsApp } from "@/lib/exportRx";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, Save, FileText, Download, MessageCircle, Loader2 } from "lucide-react";
import { computeMedTotal } from "@/lib/medUtils";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const emptyRow = () => ({ name: "", salt: "", food: "", morning: "", afternoon: "", night: "", duration: "", total_qty: "" });
const blank = () => ({ patient_name: "", age_gender: "", drug_allergy: "", date: today(), notes: "", doctor: "", medicines: Array.from({ length: 6 }, emptyRow) });

export default function Editor() {
  const [data, setData] = useState(blank());
  const [options, setOptions] = useState({});
  const [medCatalog, setMedCatalog] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [printAlign, setPrintAlign] = useState(DEFAULT_ALIGNMENT);
  const [params] = useSearchParams();
  const loadId = params.get("id");
  const location = useLocation();
  const [waOpen, setWaOpen] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedId, setSavedId] = useState(loadId || null);

  const loadOptions = useCallback(() => {
    api.get("/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadOptions();
    api.get("/medicines").then((r) => setMedCatalog((r.data || []).flatMap((g) => g.meds || []))).catch(() => {});
    api.get("/doctors").then((r) => setDoctors(r.data || [])).catch(() => {});
    api.get("/settings/alignment").then((r) => setPrintAlign({ ...DEFAULT_ALIGNMENT, offsetTop: r.data?.offsetTop || 0, offsetLeft: r.data?.offsetLeft || 0 })).catch(() => {});
  }, [loadOptions]);

  // default the doctor to Dr. Priyank (or first) on a fresh prescription
  useEffect(() => {
    if (loadId || !doctors.length) return;
    setData((d) => {
      if (d.doctor) return d;
      const pref = doctors.find((x) => /priyank/i.test(x.name)) || doctors[0];
      return { ...d, doctor: pref.name };
    });
  }, [doctors, loadId]);

  useEffect(() => {
    if (loadId) {
      api.get(`/prescriptions/${loadId}`).then((r) => {
        const d = r.data;
        const meds = d.medicines && d.medicines.length ? d.medicines : Array.from({ length: 6 }, emptyRow);
        while (meds.length < 6) meds.push(emptyRow());
        setData({ ...blank(), ...d, medicines: meds });
      }).catch(() => toast.error("Could not load prescription"));
    }
  }, [loadId]);

  // Start a fresh prescription whenever the user navigates to "/" without an
  // ?id (e.g. clicking "New Prescription"). Prevents the previous, already-saved
  // record from being overwritten. Keyed on location.key so it also fires when
  // re-clicking "New Prescription" while already on "/".
  useEffect(() => {
    if (loadId) return;
    const b = blank();
    const pref = doctors.find((x) => /priyank/i.test(x.name)) || doctors[0];
    if (pref) b.doctor = pref.name;
    setData(b);
    setSavedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const addOption = async (field, value) => {
    setOptions((o) => ({ ...o, [field]: [...(o[field] || []), value] }));
    try {
      const { data: res } = await api.post(`/options/${field}`, { value });
      setOptions((o) => ({ ...o, [field]: res.values }));
    } catch {
      toast.error("Could not save option");
    }
  };

  const deleteOption = async (field, value) => {
    setOptions((o) => ({ ...o, [field]: (o[field] || []).filter((v) => v !== value) }));
    try {
      await api.delete(`/options/${field}`, { data: { value } });
    } catch {
      toast.error("Could not delete option");
    }
  };

  const buildPayload = () => ({ ...data, medicines: data.medicines.filter((m) => m.name.trim()).map((m) => ({ ...m, total_qty: computeMedTotal(m) })) });
  const hasContent = (p) => !!(p.patient_name.trim() || p.medicines.length > 0);

  const save = async () => {
    const payload = buildPayload();
    if (!hasContent(payload)) {
      toast.error("Add a patient name or at least one medicine before saving");
      return;
    }
    try {
      if (savedId) {
        await api.put(`/prescriptions/${savedId}`, payload);
      } else {
        const { data: res } = await api.post("/prescriptions", payload);
        setSavedId(res.id);
      }
      toast.success("Prescription saved");
    } catch {
      toast.error("Failed to save prescription");
    }
  };

  // Silent save used by Print / WhatsApp. Never creates a duplicate: if this
  // prescription is already saved, it does nothing.
  const ensureSaved = async () => {
    if (savedId) return savedId;
    const payload = buildPayload();
    if (!hasContent(payload)) return null;
    try {
      const { data: res } = await api.post("/prescriptions", payload);
      setSavedId(res.id);
      return res.id;
    } catch {
      toast.error("Could not auto-save prescription");
      return null;
    }
  };

  const setDoctor = (name) => setData((d) => ({ ...d, doctor: name }));
  const selectedSig = (doctors.find((x) => x.name === data.doctor) || {}).signature || "";

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadPdf({ ...data, doctor_signature: selectedSig }, `prescription-${(data.patient_name || "patient").replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setBusy(false);
    }
  };

  const handleWhatsApp = async () => {
    setBusy(true);
    try {
      await ensureSaved();
      const res = await shareWhatsApp({ ...data, doctor_signature: selectedSig }, waPhone);
      if (res.method === "fallback") toast.message("WhatsApp opened. The PDF was downloaded so you can attach it.");
      setWaOpen(false);
    } catch {
      toast.error("Could not share to WhatsApp");
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    await ensureSaved();
    window.print();
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-6 py-3 flex flex-wrap items-center gap-3">
        <h2 className="font-app-head font-bold text-slate-800 mr-auto flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#0284c7]" /> New Prescription
        </h2>

        <Button variant="outline" onClick={save} data-testid="save-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
        <Button variant="outline" onClick={handleDownload} disabled={busy} data-testid="download-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />} Download
        </Button>
        <Button onClick={() => setWaOpen(true)} disabled={busy} data-testid="whatsapp-btn" className="bg-[#25D366] hover:bg-[#1eb857] text-white">
          <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
        </Button>
        <Button onClick={handlePrint} data-testid="print-btn" className="bg-[#0284c7] hover:bg-[#0369a1] text-white">
          <Printer className="h-4 w-4 mr-1" /> Print
        </Button>
      </div>

      {/* Entry & export layout — letterhead + content, shown on screen, hidden when printing */}
      <div className="no-print flex justify-center py-10 px-4">
        <PreprintLayer data={data} setData={setData} options={options} addOption={addOption} deleteOption={deleteOption} align={DEFAULT_ALIGNMENT} editable templateSrc="/prescription_template.png" printTemplate medCatalog={medCatalog} doctorList={doctors} doctor={data.doctor} onDoctorChange={setDoctor} signature={selectedSig} />
      </div>

      {/* Print layout — hidden on screen, used only when printing (content only, saved alignment, no letterhead) */}
      <div className="print-only" data-testid="print-layout">
        <PreprintLayer data={data} align={printAlign} editable={false} signature={selectedSig} />
      </div>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="bg-white" data-testid="whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>Send prescription on WhatsApp</DialogTitle>
            <DialogDescription>
              On phones the PDF attaches automatically via the share sheet. On desktop, WhatsApp opens and the PDF downloads so you can attach it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="wa-phone">Patient's WhatsApp number (optional)</Label>
            <Input id="wa-phone" value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="e.g. +91 98765 43210" data-testid="whatsapp-phone" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
            <Button onClick={handleWhatsApp} disabled={busy} data-testid="whatsapp-send" className="bg-[#25D366] hover:bg-[#1eb857] text-white">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <MessageCircle className="h-4 w-4 mr-1" />} Send via WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
